import React from 'react';
import { LandingNav } from '../components/Nav';
import SiteFooter from '../components/SiteFooter';
import { CHAPTER_SECTION_SCROLL_MARGIN_PX } from '../hooks/useChapterSectionNav';
import { useMediaQuery } from '../hooks/useMediaQuery';

const ActLabel = ({ text }) => (
  <div style={{ display: 'inline-flex', background: 'rgb(251,234,240)', borderRadius: '20px', padding: '4px 12px', marginBottom: '14px' }}>
    <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgb(153,53,86)' }}>{text}</span>
  </div>
);

const SectionHeader = ({ act, headline, italicLine, subhead }) => (
  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
    <ActLabel text={act} />
    <h2 className="serif" style={{ fontSize: 'clamp(24px, 2.5vw, 28px)', fontWeight: 700, textAlign: 'center', color: 'rgb(17,17,17)', lineHeight: 1.25, marginBottom: '10px' }}>
      {headline}
      <br />
      <em style={{ color: 'rgb(212,83,126)' }}>{italicLine}</em>
    </h2>
    <p style={{ fontSize: '15px', color: 'rgb(122,107,99)', textAlign: 'center', maxWidth: '540px', margin: '0 auto', lineHeight: 1.65 }}>{subhead}</p>
  </div>
);

const FeatCard = ({ emoji, title, desc, isNew, borderColor }) => (
  <div style={{ background: 'rgb(255,253,249)', border: '0.5px solid rgb(232,224,216)', borderRadius: '14px', padding: '18px', flex: 1, display: 'flex', flexDirection: 'column', borderLeft: `3px solid ${borderColor}` }}>
    <div style={{ fontSize: '22px', marginBottom: '10px' }}>{emoji}</div>
    <p style={{ fontSize: '13px', fontWeight: 700, color: 'rgb(17,17,17)', marginBottom: '5px', lineHeight: 1.3 }}>
      {title}
      {isNew && <span style={{ fontSize: '9px', fontWeight: 700, background: 'rgb(225,245,238)', color: 'rgb(15,110,86)', padding: '1px 6px', borderRadius: '8px', marginLeft: '6px', verticalAlign: 'middle' }}>New</span>}
    </p>
    <p style={{ fontSize: '12px', color: 'rgb(95,94,90)', lineHeight: 1.5, margin: 0 }}>{desc}</p>
  </div>
);

export default function FertilityChapterPage() {
  const m680 = useMediaQuery('(max-width: 680px)');

  const cycleSteps = [
    {
      dot: '✓',
      dotFontSize: '10px',
      dotBg: 'rgb(225,245,238)',
      dotColor: 'rgb(14,110,86)',
      lineColor: 'rgb(225,245,238)',
      title: 'Initial consult + baseline labs',
      subtitle: 'AMH 1.2 · AFC 9 · FSH 8.4',
      statusTag: { label: 'Done', bg: 'rgb(225,245,238)', color: 'rgb(14,110,86)' },
      feature: (
        <div style={{ background: 'rgba(249, 246, 241, 0.8)', border: '0.5px solid rgba(212,83,126,0.12)', borderRadius: '10px', padding: '10px 12px', marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>🗺️</span>
          <div>
            <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'rgb(17,17,17)' }}>IVF cycle map</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'rgb(122,107,99)', lineHeight: 1.5 }}>
              Your full cycle timeline in one place — every milestone from consult to beta result, labs, appointments, and what comes next.
            </p>
          </div>
        </div>
      ),
    },
    {
      dot: '✓',
      dotFontSize: '10px',
      dotBg: 'rgb(225,245,238)',
      dotColor: 'rgb(14,110,86)',
      lineColor: 'rgba(212,83,126,0.25)',
      title: 'Stimulation — Day 1–10',
      subtitle: 'Gonal-F 225IU + Menopur 75IU · 3 monitoring visits',
      statusTag: { label: 'Done', bg: 'rgb(225,245,238)', color: 'rgb(14,110,86)' },
      feature: (
        <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
          <div style={{ background: 'rgba(249, 246, 241, 0.8)', border: '0.5px solid rgba(212,83,126,0.12)', borderRadius: '10px', padding: '10px 12px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>💉</span>
            <div>
              <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'rgb(17,17,17)' }}>
                Medication protocol log
                <span style={{ background: 'rgb(225,245,238)', color: 'rgb(15,110,86)', borderRadius: '8px', padding: '1px 6px', fontSize: '9px', fontWeight: 700, marginLeft: '6px' }}>New</span>
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'rgb(122,107,99)', lineHeight: 1.5 }}>
                Exact doses, timing, and Nova reminders before every injection so nothing falls through.
              </p>
            </div>
          </div>
          <div style={{ background: 'rgba(249, 246, 241, 0.8)', border: '0.5px solid rgba(212,83,126,0.12)', borderRadius: '10px', padding: '10px 12px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>📊</span>
            <div>
              <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'rgb(17,17,17)' }}>
                Monitoring & lab tracker
                <span style={{ background: 'rgb(225,245,238)', color: 'rgb(15,110,86)', borderRadius: '8px', padding: '1px 6px', fontSize: '9px', fontWeight: 700, marginLeft: '6px' }}>New</span>
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'rgb(122,107,99)', lineHeight: 1.5 }}>
                Estradiol, progesterone, follicle counts logged and trended across every visit.
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      dot: '🔔',
      dotFontSize: '11px',
      dotBg: 'rgb(212,83,126)',
      dotColor: 'white',
      lineColor: 'rgba(232,224,216,0.8)',
      title: 'Trigger shot tonight',
      subtitle: '10,000 IU hCG · 11:30pm exactly',
      statusTag: { label: 'Tonight', bg: 'rgb(251,234,240)', color: 'rgb(153,53,86)' },
    },
    {
      dot: '🥚',
      dotFontSize: '11px',
      dotBg: 'rgba(232,224,216,0.6)',
      dotColor: 'rgb(122,107,99)',
      lineColor: 'rgba(232,224,216,0.8)',
      title: 'Egg retrieval',
      subtitle: 'Arrive 7am · 36hrs after trigger',
      statusTag: { label: 'Tomorrow', bg: 'rgb(232,224,216)', color: 'rgb(122,107,99)' },
    },
    {
      dot: '🧫',
      dotFontSize: '11px',
      dotBg: 'rgba(232,224,216,0.6)',
      dotColor: 'rgb(122,107,99)',
      lineColor: 'rgba(232,224,216,0.8)',
      title: 'Fertilization report',
      subtitle: 'Day 1 call from embryology lab — Day +1',
    },
    {
      dot: '🔬',
      dotFontSize: '11px',
      dotBg: 'rgba(232,224,216,0.6)',
      dotColor: 'rgb(122,107,99)',
      title: 'Day 5 blast + PGT results',
      subtitle: 'Embryo grades and chromosomal results — Day +5',
    },
  ];

  const learnFeatures = [
    { emoji: '🧬', title: 'Lab result decoder', desc: 'AMH, FSH, AFC, estradiol — Nova explains every number and what it means for your cycle.', isNew: false, borderColor: 'rgb(238,237,254)' },
    { emoji: '🔬', title: 'Embryo grading explainer', desc: 'Day 3 and Day 5 grades decoded — expansion, ICM, trophectoderm — and what they mean for transfer success.', isNew: true, borderColor: 'rgb(251,234,240)' },
    { emoji: '🤔', title: '"Is this normal?" checker', desc: 'OHSS symptoms, spotting after transfer — Nova answers relative to your cycle day and protocol.', isNew: true, borderColor: 'rgb(225,245,238)' },
    { emoji: '📋', title: 'Appointment prep builder', desc: 'Nova generates the right questions for your next RE visit based on your current results and protocol.', isNew: true, borderColor: 'rgb(255,241,221)' },
  ];


  const decideTimeline = [
    {
      emoji: '🧬',
      dotLabel: 'Baseline',
      dotBg: 'rgb(251, 234, 240)',
      dotText: 'rgb(153, 53, 86)',
      borderColor: 'rgb(153, 53, 86)',
      title: 'Understand your baseline',
      desc: 'AMH, FSH, AFC decoded before you choose a protocol, clinic, or next step.',
    },
    {
      emoji: '💰',
      dotLabel: 'Financials',
      dotBg: 'rgb(255, 241, 221)',
      dotText: 'rgb(146, 84, 10)',
      borderColor: 'rgb(146, 84, 10)',
      title: 'Plan your financials',
      desc: 'Every cost tracked in real time. Grants and financing surfaced as you go.',
    },
    {
      emoji: '🏥',
      dotLabel: 'Clinic',
      dotBg: 'rgb(225, 245, 238)',
      dotText: 'rgb(14, 110, 86)',
      borderColor: 'rgb(14, 110, 86)',
      title: 'Choose and prep your clinic',
      desc: 'Success rates, questions, red flags — walk into your appointment prepared.',
    },
    {
      emoji: '💉',
      dotLabel: 'Stims',
      dotBg: 'rgb(238, 237, 254)',
      dotText: 'rgb(88, 58, 168)',
      borderColor: 'rgb(88, 58, 168)',
      title: 'Navigate your stims cycle',
      desc: 'Protocol saved, reminders set, monitoring logged every day of treatment.',
    },
    {
      emoji: '🔬',
      dotLabel: 'Results',
      dotBg: 'rgb(219, 234, 254)',
      dotText: 'rgb(29, 78, 216)',
      borderColor: 'rgb(29, 78, 216)',
      title: 'Understand your results',
      desc: 'Blast grades, PGT, beta numbers — your report explained in plain language.',
    },
    {
      emoji: '🌿',
      dotLabel: 'Paths Forward',
      dotBg: 'rgb(254, 226, 226)',
      dotText: 'rgb(185, 28, 28)',
      borderColor: 'rgb(185, 28, 28)',
      title: 'Explore every path forward',
      desc: 'After a failed cycle — egg donation, surrogacy, adoption explained clearly.',
    },
  ];

  const sectionWhite = { padding: '52px 48px', backgroundColor: 'rgb(255,252,249)' };
  const sectionCream = { padding: '52px 48px', backgroundColor: 'rgb(249,246,241)' };
  const sectionAnchor = { scrollMarginTop: '80px' };
  const inner = { maxWidth: '1100px', margin: '0 auto' };
  const pink = 'rgb(212,83,126)';
  return (
    <div style={{ background: 'rgb(255,252,249)' }}>
      <div style={{ background: 'var(--white)', color: 'var(--text)' }}>
        <LandingNav />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '480px' }}>
        <div
          style={{
            backgroundImage: "url('/images/chapter-heroes/fertility.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            minHeight: '480px',
          }}
        />
        <div style={{ background: pink, padding: '52px 48px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.15)', borderRadius: '20px', padding: '5px 12px', marginBottom: '16px', width: 'fit-content' }}>
            <span style={{ fontSize: '16px' }}>🌸</span>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'white' }}>Fertility</span>
            <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.15)', padding: '2px 8px', borderRadius: '10px' }}>Coming Summer 2026</span>
          </div>
          <h1 className="serif" style={{ fontSize: 'clamp(26px, 3vw, 36px)', fontWeight: 700, color: 'white', lineHeight: 1.2, marginBottom: '12px' }}>
            No two journeys are the same.{` `}
            <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)' }}>OrivonAI is built for yours.</em>
          </h1>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, marginBottom: '20px', maxWidth: '440px' }}>
            Protocol tracking, result decoding, financial tools, community matched to your exact cycle stage, and specialist matching — built around the path you&apos;re actually on, not the average fertility patient.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={{ background: 'white', color: pink, fontSize: '13px', fontWeight: 700, padding: '10px 20px', borderRadius: '24px', border: 'none', cursor: 'pointer' }}>Join the waitlist</button>
            <button style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: 'white', fontSize: '13px', fontWeight: 600, padding: '10px 20px', borderRadius: '24px', cursor: 'pointer' }}>See how it works</button>
          </div>
        </div>
      </div>

      <div id="chapter-track" style={{ ...sectionWhite, ...sectionAnchor }}>
        <div style={inner}>
          <SectionHeader act="Track" headline="Your IVF cycle, mapped." italicLine="Every step, before you have to ask." subhead="OrivonAI holds your full protocol — injections, monitoring, retrieval, transfer — and checks in proactively so you're never surprised by what comes next." />
          <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
            <div style={{ background: 'rgb(255, 253, 249)', border: '1px solid rgba(212, 83, 126, 0.2)', borderRadius: '16px', overflow: 'hidden', width: '100%' }}>
              <div style={{ padding: '14px 22px', borderBottom: '0.5px solid rgb(232, 224, 216)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p style={{ margin: 0, fontSize: '10px', fontWeight: 700, color: 'rgb(153, 53, 86)', letterSpacing: '1px', textTransform: 'uppercase' }}>Noelle&apos;s IVF cycle — Cycle 1</p>
                <span style={{ fontSize: '10px', fontWeight: 600, color: 'rgb(153, 53, 86)', background: 'rgb(251, 234, 240)', borderRadius: '999px', padding: '2px 9px' }}>Stim day 10 of 10</span>
              </div>
              <div style={{ padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 0 }}>
                {cycleSteps.map((step, idx) => (
                  <div key={step.title} style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: '12px', position: 'relative' }}>
                    <div style={{ position: 'relative' }}>
                      <div style={{ width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: step.dotFontSize, zIndex: 1, flexShrink: 0, marginTop: '2px', background: step.dotBg, color: step.dotColor }}>
                        {step.dot}
                      </div>
                      {idx < cycleSteps.length - 1 && (
                        <div style={{ position: 'absolute', left: '9px', top: '22px', bottom: '-8px', width: '2px', background: step.lineColor }} />
                      )}
                    </div>
                    <div style={{ paddingBottom: '16px' }}>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: 'rgb(17, 17, 17)' }}>{step.title}</p>
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'rgb(122, 107, 99)' }}>{step.subtitle}</p>
                      {step.statusTag && (
                        <span style={{ fontSize: '9px', fontWeight: 700, borderRadius: '999px', padding: '1px 7px', marginTop: '4px', display: 'inline-block', background: step.statusTag.bg, color: step.statusTag.color }}>
                          {step.statusTag.label}
                        </span>
                      )}
                      {step.feature}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: 'rgb(251, 234, 240)', borderTop: '0.5px solid rgba(212, 83, 126, 0.2)', padding: '14px 22px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'rgb(212, 83, 126)', color: 'white', fontSize: '9px', fontWeight: 900, fontFamily: "'Playfair Display', serif", display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                  N
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: 'rgb(153, 53, 86)' }}>Nova&apos;s reminder for tonight</p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'rgb(153, 53, 86)', lineHeight: 1.5 }}>
                    Your trigger shot must be at exactly 11:30pm. I&apos;ll send you a reminder at 11:15pm. Your retrieval is at 7am tomorrow.
                  </p>
                </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <div style={{ height: '1px', background: 'rgb(232,224,216)' }} />

      <div id="chapter-learn" style={{ ...sectionCream, ...sectionAnchor }}>
        <div style={inner}>
          <SectionHeader act="Learn" headline="Your results, explained." italicLine="Not just what they are — what they mean." subhead="The fertility system gives you numbers. Nova tells you what they actually mean for your specific cycle, your protocol, and your next decision." />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '14px', alignItems: 'stretch' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {learnFeatures.slice(0, Math.ceil(learnFeatures.length / 2)).map((f) => <FeatCard key={f.title} {...f} />)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {learnFeatures.slice(Math.ceil(learnFeatures.length / 2)).map((f) => <FeatCard key={f.title} {...f} />)}
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: '1px', background: 'rgb(232,224,216)' }} />

      <div id="chapter-connect" style={{ ...sectionWhite, ...sectionAnchor }}>
        <div style={inner}>
          <SectionHeader act="Connect" headline="Women living your cycle." italicLine="Not just your chapter." subhead="Nova matches you to women on your exact protocol, at your exact stage — retrieval night, TWW, waiting on blasts — and opens live rooms for the moments that can't wait." />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', alignItems: 'stretch' }}>
            <div style={{ background: 'rgb(255,253,249)', borderRadius: '12px', padding: '18px', border: '0.5px solid rgb(232,224,216)', borderLeft: '2px solid rgb(251, 234, 240)', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgb(251, 234, 240)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, flexShrink: 0 }} aria-hidden>🏡</div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'rgb(17,17,17)' }}>Orivon Spaces</h3>
              </div>
              <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', borderRadius: 999, padding: '2px 9px', fontSize: 9, fontWeight: 700, background: 'rgb(251, 234, 240)', color: 'rgb(153, 53, 86)' }}>
                ✦ Matched to your chapter
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.52, color: 'rgb(122,107,99)' }}>
                Ongoing communities tailored to your reproductive journey, where the conversations, support, and resources you find are relevant to where you are right now.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {['IVF Warriors', 'PCOS Sisters', 'After Loss'].map((label) => (
                  <span key={label} style={{ background: 'rgb(251, 234, 240)', color: 'rgb(153, 53, 86)', borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 500 }}>
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ background: 'rgb(255,253,249)', borderRadius: '12px', padding: '18px', border: '0.5px solid rgb(232,224,216)', borderLeft: '2px solid rgb(225, 245, 238)', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgb(225, 245, 238)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, flexShrink: 0 }} aria-hidden>🎙️</div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'rgb(17,17,17)' }}>Orivon Live</h3>
              </div>
              <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 999, padding: '2px 9px', fontSize: 9, fontWeight: 700, background: 'rgb(225, 245, 238)', color: 'rgb(14, 110, 86)' }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgb(34, 197, 94)', flexShrink: 0 }} aria-hidden />
                Live now
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.52, color: 'rgb(122,107,99)' }}>
                Real-time rooms that open for the moments that can&apos;t wait — retrieval night, the two-week wait, a negative beta. The community shows up when you need it most.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {['Retrieval check-ins', 'TWW support', 'After a loss'].map((label) => (
                  <span key={label} style={{ background: 'rgb(225, 245, 238)', color: 'rgb(14, 110, 86)', borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 500 }}>
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ background: 'rgb(255,253,249)', borderRadius: '12px', padding: '18px', border: '0.5px solid rgb(232,224,216)', borderLeft: '2px solid rgb(238, 237, 254)', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgb(238, 237, 254)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, flexShrink: 0 }} aria-hidden>🤝</div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'rgb(17,17,17)' }}>Orivon Connect</h3>
              </div>
              <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', borderRadius: 999, padding: '2px 9px', fontSize: 9, fontWeight: 700, background: 'rgb(238, 237, 254)', color: 'rgb(88, 58, 168)' }}>
                98% match accuracy
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.52, color: 'rgb(122,107,99)' }}>
                One-to-one matching with women at your exact stage. The platform knows your protocol, your chapter, your history — and finds the woman living it right alongside you.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {['Same protocol', 'Same week', 'Same chapter'].map((label) => (
                  <span key={label} style={{ background: 'rgb(238, 237, 254)', color: 'rgb(88, 58, 168)', borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 500 }}>
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: '1px', background: 'rgb(232,224,216)' }} />

      <div id="chapter-decide" style={{ ...sectionCream, ...sectionAnchor }}>
        <div style={inner}>
          <SectionHeader act="Decide" headline="Your next step, made clear." italicLine="Whatever the result." subhead="Failed cycles, low embryo counts, unexpected results — The platform helps you understand what happened, what your options are, and how to move forward with confidence." />

          <div style={{ width: '100%', maxWidth: '1000px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', flexWrap: m680 ? 'wrap' : 'nowrap', rowGap: m680 ? '16px' : 0 }}>
              {decideTimeline.map((node, idx) => (
                <div
                  key={node.dotLabel}
                  style={{
                    flex: m680 ? '0 0 calc(33% - 8px)' : 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {!m680 && idx < decideTimeline.length - 1 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '24px',
                        left: 'calc(50% + 25px)',
                        width: 'calc(100% - 50px)',
                        height: '2px',
                        background: 'rgba(212, 83, 126, 0.12)',
                      }}
                    />
                  )}
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0, background: node.dotBg }}>
                    {node.emoji}
                  </div>
                  <p style={{ margin: 0, fontSize: '10px', fontWeight: 700, textAlign: 'center', lineHeight: 1.3, maxWidth: '80px', color: node.dotText }}>{node.dotLabel}</p>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: m680 ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)', gap: '8px' }}>
              {decideTimeline.map((card) => (
                <div key={card.title} style={{ background: 'rgb(255, 253, 249)', borderRadius: '12px', padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: '6px', borderTop: `3px solid ${card.borderColor}` }}>
                  <p style={{ margin: 0, fontSize: '11.5px', fontWeight: 700, color: 'rgb(17, 17, 17)', lineHeight: 1.35 }}>{card.title}</p>
                  <p style={{ margin: 0, fontSize: '10.5px', color: 'rgb(122, 107, 99)', lineHeight: 1.55 }}>{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: pink, padding: '52px 48px', textAlign: 'center' }}>
        <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', marginBottom: '10px' }}>Start your fertility journey</p>
        <h2 className="serif" style={{ fontSize: 'clamp(24px, 2.5vw, 30px)', fontWeight: 700, color: 'white', lineHeight: 1.25, marginBottom: '10px' }}>
          You deserve support for every part of this.
          <br />
          <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)' }}>The questions, the results, and the hard nights.</em>
        </h2>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)', maxWidth: '460px', margin: '0 auto 24px', lineHeight: 1.6 }}>
          IVF cycles, embryo results, failed transfers — OrivonAI starts with where you are right now and stays with you through all of it.
        </p>
        <button style={{ background: 'white', color: pink, fontSize: '13px', fontWeight: 700, padding: '11px 28px', borderRadius: '24px', border: 'none', cursor: 'pointer' }}>
          Join the waitlist
        </button>
      </div>

      <SiteFooter linkColor="var(--muted)" />
    </div>
  );
}
